/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Api, ApiQueryResponse, ApiQuery } from 'app/shared/lib/essette-api';
import { StandardLoadOptions } from 'app/shared/lib/essette-site';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { EnvironmentService } from 'app/shared/services/environment-service';

//TODO: This stuff should be moved to a CORE task-appointment-service
@autoinject
export class AppointmentService {

    constructor(private api: Api, private readonly env: EnvironmentService) { }


    public async loadQueueProviders(): Promise<ApiQueryResponse<CD.Provider>> {

        let query = new ApiQuery('cd/providers')
            .filterExists('applications');

        return await this.api.get(query);
    }

    public async search(searchFilters: IAppointmentSearchFilters, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<ApiQueryResponse<CM.TaskAppointment> | null> {
        if(!searchFilters) { return null };

        const query = new ApiQuery('cm/tasks/appointments')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchFilters.dateStartTime) {
            query.filterDateTimeBetweenDates(
                'dateTimeRange.start',
                searchFilters.dateStartTime,
                searchFilters.dateEndTime || null);
        }
        if(searchFilters.type && searchFilters.type.code) {
            query.filterString('task.attachedTo.type.code', searchFilters.type.code);
        }
        if(searchFilters.caseNumber) {
            query.filterString('case.number', searchFilters.caseNumber, 'startsWith');
        }
        if(searchFilters.memberId) {
            query.filterString('case.member.identifier.primary', searchFilters.memberId, 'startsWith');
        }
        if(searchFilters.memberMedicaidId) {
            query.filterString('case.member.identifier.medicaid', searchFilters.memberMedicaidId, 'startsWith');
        }
        if(searchFilters.memberFirstName) {
            query.filterString('case.member.name.first', searchFilters.memberFirstName, 'startsWith');
        }
        if(searchFilters.memberLastName) {
            query.filterString('case.member.name.last', searchFilters.memberLastName, 'startsWith');
        }
        if(searchFilters.queue && searchFilters.queue.id) {
            query.filterString('task.delegated.queue.id', searchFilters.queue.id);
        }

        let myFilterCount = 0;
        if(searchFilters.myCases) myFilterCount++;
        if(searchFilters.delegatedToMe) myFilterCount++;
        if(searchFilters.myCareTeam) myFilterCount++;
        if(myFilterCount <= 1) {
            // if 0-1 "My" filters selected, run each filter individually
            if(searchFilters.assignedTo) {
                query.filterString('case.assignedTo.id', searchFilters.assignedTo.id || null);
            }
            if(searchFilters.delegatedToUser || searchFilters.delegatedToProvider) {
                query.filterString('task.delegated.to.id', searchFilters.delegatedToUser?.id || searchFilters.delegatedToProvider?.id || null);
            }
            if(searchFilters.careTeamParticipant) {
                query.filterExists('case.careteam.participants', (f =>
                    f.filterString('assigned.toUser.id', searchFilters.careTeamParticipant?.id!, 'eq')
                ));
            }
        } else {
            // if more than one "My" filter is selected, construct the filterOr on the query
            query.filterOr((or) => {
                if(searchFilters.myCases) {
                    or.filterString('case.assignedTo.id', searchFilters?.assignedTo?.id || null);
                }
                if(searchFilters.delegatedToMe) {
                    or.filterString('task.delegated.to.id', searchFilters?.delegatedToUser?.id || searchFilters?.delegatedToProvider?.id || null);
                }
                if(searchFilters.myCareTeam) {
                    or.filterExists('case.careteam.participants', (f =>
                        f.filterString('assigned.toUser.id', searchFilters.careTeamParticipant?.id!, 'eq')
                    ));
                }
            });
            // and then filter based on any additional "non-My" fields that may be selected
            if(!searchFilters.myCases && searchFilters.assignedTo) {
                query.filterString('case.assignedTo.id', searchFilters.assignedTo.id || null);
            }
            if(searchFilters.delegatedToUser || searchFilters.delegatedToProvider) {
                query.filterString('task.delegated.to.id', searchFilters.delegatedToUser?.id || searchFilters.delegatedToProvider?.id || null);
            }
            if(!searchFilters.myCareTeam && searchFilters.careTeamParticipant) {
                query.filterExists('case.careteam.participants', (f =>
                    f.filterString('assigned.toUser.id', searchFilters.careTeamParticipant?.id!, 'eq')
                ));
            }
        }

        if(searchFilters.delegatedBy && searchFilters.delegatedBy.id) {
            query.filterString('task.metadata.delegated.byUser.id', searchFilters.delegatedBy.id);
        }
        if(searchFilters.eligibility) {
            query.filterBoolean('member.eligibility.isCurrent', true);
        }
        if(searchFilters.subject) {
            query.filterString('text', searchFilters.subject, 'contains');
        }
        if(searchFilters.description) {
            query.filterString('task.description', searchFilters.description, 'contains');
        }

        return await this.api.get(query);
    }
}

export interface IAppointmentSearchFilters {
    date?: Date | null;
    dateStartTime?: string | null;
    dateEndTime?: string | null;
    type?: CM.TaskTargetType | null;
    memberId?: string | null;
    memberMedicaidId?: string | null;
    memberFirstName?: string | null;
    memberLastName?: string | null;
    eligibility?: boolean | null;
    subject?: string | null;
    description?: string | null;
    queue?: Core.Queue | null;
    assignedTo?: Core.User | null;
    delegatedToMe?: boolean | null;
    delegatedToUser?: Core.User | null;
    delegatedToProvider?: CD.Provider | null;
    delegatedBy?: Core.User | null;
    caseNumber?: string | null;
    myCases?: boolean | null;
    myCareTeam?: boolean | null;
    careTeamParticipant?: Core.User | null;
}
